import React, { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AppBar, DLCForm } from 'te-components';
import { path } from 'routes/path';
import {
  navigationRoutes,
  inAccessibleKeys,
  inboxTypeKeys,
  managerAccessibleKeys,
  inboxAccountName,
  reporterAccessibleKeys,
  totalAccessibleKeys,
  dlcFormStatus,
  stockExchangesOption,
  businessTypeOption,
  TEN,
  THREE,
  dlcErrorStates,
} from 'appConstants';
import {
  formatPhoneNumber,
  hasManagerRole,
  hasCustomerAdmin,
  hasReporterRole,
  hasJobChat,
} from 'util/helpers';
import {
  handleNavItemClick,
  handleTenDLCFormClick,
  handleChatClick,
  handleClaimAllChatClick,
  handleHelpOnClick,
  handleUnreadMessageClick,
  handleViewAllUnreadClick,
  handleMenuIconClick,
} from './appBarHelper';
import { createDLCForm, fetchDLCForm, enableDlcForm } from 'redux/actions';
import { logout } from 'redux/actions/user';
import DLCModal from './DLCModal';
import { useMediaBreakpoint } from 'customHooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import SMSProviderUI from 'components/SMSProviderUI';
import {
  reformatDlcFormData,
  businessTypes,
  regionsOfOperation,
  companyStatusTypes,
  jobPositionTypes,
  businessIndustries,
  businessRegistrationTypes,
  stockExchanges,
  initializePendo,
  webReactOptOutTracker,
  updateLoginPendoTrack,
  getLengthLimitedUsername,
  getUserNavbarPreferences,
} from './helpers';
import { countriesList } from './countriesList';
import { show10DLCForm, handleSubmit, onDLCModalClose, getErrorField } from './tenDLCHelper';
import { useFetchDLCData } from './useFetchDLCData';
import { windowStore } from 'util/windowStore';
import FaviconBadge from 'util/FaviconBadge';
import { actions, sideBarReducer, sideBarInitialState } from './reducer';
import { updateUnreadMessagesCountInDocumentTitle } from 'util/common';

const favicon = new FaviconBadge({});

const enableEditDlcForm = (tenDlcData) => {
  let lowercasedErrorField = '';
  let canRetryBrandSubmission = false;

  // If the api has brand registration in the errorfield and the brandretrycount value is less than 3 or undefined, the form has to be disabled.
  //  The user will contact support team to reenable the form.
  // When the errorField has brand registration, it cannot have any other value in the array
  if (tenDlcData.errorFields && tenDlcData.errorFields.length) {
    [lowercasedErrorField] = tenDlcData.errorFields.map((item) => item.toLowerCase());
    canRetryBrandSubmission = !tenDlcData.brandRetryCount || tenDlcData.brandRetryCount < THREE;
  }

  const isBrandSubmissionRetryNeeded =
    lowercasedErrorField === dlcErrorStates.brandRegistration && canRetryBrandSubmission;

  return (
    (!tenDlcData.enableEditForSubmittedForm &&
      tenDlcData.providerStatus === dlcFormStatus.SUBMITTED) ||
    tenDlcData.providerStatus === dlcFormStatus.INPROGRESS ||
    isBrandSubmissionRetryNeeded
  );
};

const getPOCData = (data) => {
  const pocObj = {};

  data.peopleToContact.forEach((contact, index) => {
    pocObj[`firstNamePOC${index + 1}`] = contact.firstName;
    pocObj[`lastNamePOC${index + 1}`] = contact.lastName;
    pocObj[`emailPOC${index + 1}`] = contact.email;
    pocObj[`countryCodePOC${index + 1}`] = contact.countryCode;
    pocObj[`phoneNumberPOC${index + 1}`] = contact.phoneNumber;
    pocObj[`titlePOC${index + 1}`] = contact.businessTitle;
    pocObj[`jobPositionPOC${index + 1}`] = contact.jobPosition;
  });

  return { ...pocObj, disablePOC2: data.peopleToContact.length <= 1 };
};

/* eslint-disable complexity */
const getUserInAccessibleKeys = ({
  inboxAccessList,
  canViewEvents,
  customerIds,
  has10DlcFormAccess,
  isReporter,
  isCustomerAdmin,
  isManager,
  smsDisabled,
  showCampaignsOverride,
  enableTeams,
}) => {
  const allInboxes = [inboxTypeKeys.TEAM, inboxTypeKeys.USER];
  const allInboxesAccountName = [inboxAccountName.team, inboxAccountName.user];

  // basic inboxes are my inbox, team inbox. additonal inboxes are user inboxes added in console admin or users page
  const inboxes = inboxAccessList.map((inbox) => inbox.accountName);
  const basicInboxes = inboxAccessList.filter((inbox) =>
    allInboxesAccountName.includes(inbox.accountName),
  );
  const additionalInboxes = inboxes.filter((inbox) => !allInboxesAccountName.includes(inbox));

  const inboxesAccessible = basicInboxes.map((inbox) => inbox.accountType.toLowerCase());

  const inboxesInAccessible = allInboxes.filter((inbox) => !inboxesAccessible.includes(inbox));
  const inboxesInAccessibleKeys = inboxesInAccessible.map((inbox) => inAccessibleKeys[inbox]);

  const jobChatInAccessibleKey = hasJobChat(customerIds) ? [] : [inAccessibleKeys.jobChat];
  const eventsInAccessibleKey = canViewEvents ? [] : [inAccessibleKeys.events];
  const campaignsInAccessibleKey =
    smsDisabled && !showCampaignsOverride ? [inAccessibleKeys.campaigns] : [];

  // we are checking permissions only for manager, customer admin and reporter. user, customer wide user, team wide user have no permissions for reporting, downloads, users.
  const roleAccessibleKey = [];
  if (has10DlcFormAccess) {
    roleAccessibleKey.push(...managerAccessibleKeys);
  } else if (isReporter) {
    roleAccessibleKey.push(...reporterAccessibleKeys);
  }

  const isManagerWithEnabledTeams = isManager && enableTeams;
  const isCustomerAdminWithEnabledTeams = isCustomerAdmin && enableTeams;
  if (isManagerWithEnabledTeams || isCustomerAdminWithEnabledTeams) {
    roleAccessibleKey.push('teams');
  }

  const roleInAccessibleKey = totalAccessibleKeys.filter((key) => !roleAccessibleKey.includes(key));

  // For role user, team inbox is hidden so the navigation routes will be decremented by 1
  let index = 1;
  if (inboxesInAccessibleKeys.includes(inAccessibleKeys.team)) {
    navigationRoutes.myInbox = `${path.INBOX}/0`;
    delete navigationRoutes.teamInbox;
    index -= 1;
  }

  // For user with no license, my inbox is hidden so the navigation routes will be decremented by 1
  if (inboxesInAccessibleKeys.includes(inAccessibleKeys.user)) {
    index -= 1;
  }

  for (let i = 1; i <= additionalInboxes.length; i++) {
    navigationRoutes[`inbox${i}`] = `${path.INBOX}/${i + index}`;
  }

  return {
    userInAccessibleKeys: [
      ...inboxesInAccessibleKeys,
      ...jobChatInAccessibleKey,
      ...roleInAccessibleKey,
      ...eventsInAccessibleKey,
      ...campaignsInAccessibleKey,
    ],
    additionalInboxes,
    isManagerWithEnabledTeams,
    isCustomerAdminWithEnabledTeams,
  };
};

// TODO: Maximum statements in an arrow function to be fixed
// eslint-disable-next-line max-statements, complexity
const Sidebar = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const tabletView = useMediaBreakpoint('lg');
  const { t } = useTranslation();
  const [sideBarState, sideBarDispatch] = useReducer(sideBarReducer, sideBarInitialState);

  const {
    showSuccessModal,
    showSaveModal,
    switchStatus,
    showScheduleMessageModal,
    showErrors,
    errorContainer,
    stepErrorArray,
  } = sideBarState;

  const {
    twilio10DlcRegistrationStatus,
    frontendTeamRealtimeSubscription,
    frontendEnable10Dlc,
    showCampaignsOverride,
    iCimsBrandRefreshTextEngagement: iCIMSBrandRefreshTextEngagement,
  } = useFlags();

  const { user, thread, dlcForm, jobChat, campaigns } = useSelector((state) => state);

  const {
    customerId,
    id,
    customerIds,
    smsProvider,
    customerInfo,
    userName,
    smsDisabled,
    inboxAccessList,
    enableTeams,
    hasLicense,
  } = user;

  const { tenDlcData, error, isSuccess } = dlcForm;
  const { unClaimedChats } = jobChat;
  const { showDlcForm } = campaigns;

  const {
    team: { unreadThreads: teamInboxUnreadThreads },
    user: { unreadThreads },
    threadMessages: { inboxUnreadMessageCount, teamInboxUnreadMessageCount },
    inboxAccessListWithThreads,
  } = thread;

  const bellNotificationUnreadThreads = hasLicense ? unreadThreads : teamInboxUnreadThreads;
  const userUnreadThreadCountPerPage = bellNotificationUnreadThreads.length;

  const handleShowSuccessModal = (showSucessModalValue) => {
    sideBarDispatch({
      type: actions.setShowSuccessModal,
      payload: showSucessModalValue,
    });
  };
  const handleShowSaveModal = (showSaveModalValue) => {
    sideBarDispatch({
      type: actions.setShowSaveModal,
      payload: showSaveModalValue,
    });
  };
  const handleSwitchStatus = (switchStatusValue) => {
    sideBarDispatch({
      type: actions.setSwitchStatus,
      payload: switchStatusValue,
    });
  };
  const handleShowScheduleMessageModal = (showScheduleMessageModalValue) => {
    sideBarDispatch({
      type: actions.setShowScheduleMessageModal,
      payload: showScheduleMessageModalValue,
    });
  };

  const handleShowErrors = (showErrorsValue) => {
    sideBarDispatch({ type: actions.setShowErrors, payload: showErrorsValue });
  };

  const handleErrorContainer = (errorContainerValue) => {
    sideBarDispatch({
      type: actions.updateErrorContainer,
      payload: errorContainerValue,
    });
  };

  const handleStepErrorArray = (stepErrorArrayValue) => {
    sideBarDispatch({
      type: actions.updateStepErrorArray,
      payload: stepErrorArrayValue,
    });
  };

  const handleResetErrorContainer = () => {
    sideBarDispatch({ type: actions.resetErrorContainer });
  };
  const handleResetStepErrorArray = () => {
    sideBarDispatch({ type: actions.resetStepErrorArray });
  };

  const handleLogout = () => dispatch(logout());

  // Role based restrictions
  const isManager = hasManagerRole(customerIds);
  const isReporter = hasReporterRole(customerIds);
  const isCustomerAdmin = hasCustomerAdmin(customerIds);
  const has10DlcFormAccess = isManager || isCustomerAdmin;
  const canViewEvents = customerInfo.eventsEnabled;

  let formattedPhoneNumber = '';
  let ISO3166CountryCode = '';

  try {
    const {
      formattedPhoneNumber: formattedPhoneNumberObj,
      ISO3166CountryCode: ISO3166CountryCodeObj,
    } = formatPhoneNumber(user.smsPhoneNumber);
    formattedPhoneNumber = formattedPhoneNumberObj;
    ISO3166CountryCode = ISO3166CountryCodeObj;
  } catch (_) {
    // fall back to unformatted phone number
    formattedPhoneNumber = user.smsPhoneNumber;
  }

  // fetch DLC Form
  useFetchDLCData({ has10DlcFormAccess, fetchDLCForm, customerId });

  const { isLoading } = useSelector((state) => state.dlcForm);

  React.useEffect(() => {
    getErrorField({
      data: tenDlcData,
      handleShowErrors,
      handleStepErrorArray,
      handleErrorContainer,
      t,
    });
  }, [tenDlcData, t]);

  React.useEffect(() => {
    initializePendo(user, customerInfo);
    updateLoginPendoTrack(user);
  }, [user, customerInfo]);

  React.useEffect(() => {
    favicon.showBadge =
      Number.isInteger(userUnreadThreadCountPerPage) && userUnreadThreadCountPerPage !== 0;
    updateUnreadMessagesCountInDocumentTitle(userUnreadThreadCountPerPage);
  }, [userUnreadThreadCountPerPage]);

  const oldAppUrl = window.location.href.replace('/web-react', '');

  const {
    userInAccessibleKeys,
    additionalInboxes,
    isManagerWithEnabledTeams,
    isCustomerAdminWithEnabledTeams,
  } = getUserInAccessibleKeys({
    inboxAccessList,
    canViewEvents,
    customerIds,
    has10DlcFormAccess,
    isReporter,
    isCustomerAdmin,
    isManager,
    smsDisabled,
    showCampaignsOverride,
    enableTeams,
  });

  const menuList = [
    {
      label: t('profile_settings', 'Profile Settings'),
      onMenuItemClick: () => history.push(path.PROFILE),
    },
    {
      label: t('preferences', 'Preferences'),
      onMenuItemClick: () => history.push(path.SETTINGS),
    },
    {
      label: t('help_center', 'Help Center'),
      onMenuItemClick: () => handleHelpOnClick(),
    },
  ];

  if (customerInfo.enableRegulatoryBundle && (isManager || isCustomerAdmin)) {
    menuList.push({
      label: t('regulatory_bundles', 'Regulatory Bundles'),
      onMenuItemClick: () => history.push(path.REGULATORY_BUNDLES),
    });
  }

  if (has10DlcFormAccess && frontendEnable10Dlc) {
    menuList.push(
      show10DLCForm({
        handleTenDLCFormClick: () => handleTenDLCFormClick(handleShowScheduleMessageModal),
        twilio10DlcRegistrationStatus,
        tenDlcData,
        t,
      }),
    );
  }

  const menuOpened = getUserNavbarPreferences(userName);

  // inboxUnreadMessageCount has the total unread messages for the inbox and it is not updated in runtime
  // userUnreadThreadCountPerPage contains unread messages for the user only for one page (10 records) and this is updated in runtime
  const totalUnreadMessageCount =
    inboxUnreadMessageCount > TEN ? inboxUnreadMessageCount : userUnreadThreadCountPerPage;

  // teamInboxUnreadThreads contains unread message count for team and is updated at runtime
  const totalTeamUnreadMessageCount =
    teamInboxUnreadMessageCount > TEN
      ? teamInboxUnreadMessageCount
      : teamInboxUnreadThreads?.length;

  return (
    <>
      <AppBar
        data-testid="AppBar"
        onNavItemClick={(route) => handleNavItemClick({ route, history })}
        /* TODO: Implement permissions on the app bar contents */
        inAccessibleList={userInAccessibleKeys}
        candidateName={getLengthLimitedUsername(user.userName)}
        contactNumber={
          <SMSProviderUI formattedPhoneNumber={formattedPhoneNumber} smsProvider={smsProvider} />
        }
        countryCode={ISO3166CountryCode.toLowerCase()}
        handleSignOutOnClick={handleLogout}
        inboxes={additionalInboxes}
        menuOptionList={menuList}
        navigationRoutes={navigationRoutes}
        pathName={location.pathname}
        teamInboxMessageCount={
          frontendTeamRealtimeSubscription ? totalTeamUnreadMessageCount : null
        }
        myInboxMessageCount={totalUnreadMessageCount}
        inboxAccessList={inboxAccessListWithThreads}
        notificationProps={{
          unreadMessages: bellNotificationUnreadThreads,
          chats: unClaimedChats,
          totalNotifications: totalUnreadMessageCount + unClaimedChats.length,
          onUnreadMessageClick: (threadData) =>
            handleUnreadMessageClick({
              threadData,
              navigationRoutes,
              tabletView,
              history,
            }),
          onViewAllUnreadClick: () => handleViewAllUnreadClick({ history, navigationRoutes }),
          onChatClick: (chat) => handleChatClick({ chat, history, dispatch, navigationRoutes }),
          onClaimAllChatsClick: () =>
            handleClaimAllChatClick({
              history,
              navigationRoutes,
              unClaimedChats,
              dispatch,
            }),
        }}
        switchStatus={switchStatus}
        handleSwitch={() => {
          handleSwitchStatus(false);
          localStorage.setItem('angularOptedIn', true);
          webReactOptOutTracker(user, customerInfo);
          // TODO: have to test this out once the SB changes are merged - Rahul
          window.location.href = oldAppUrl;
          // window.open(oldAppUrl, '_href')
        }}
        showSwitch
        showDivider={isManager || isCustomerAdmin || isReporter}
        handleMenuIconClick={(value) => handleMenuIconClick({ value, windowStore, userName })}
        isMenuOpened={!menuOpened}
        enableTeams={isManagerWithEnabledTeams || isCustomerAdminWithEnabledTeams}
        iCIMSBrandRefresh={iCIMSBrandRefreshTextEngagement}
      />
      {
        <DLCModal
          showSuccessModal={showSuccessModal}
          showSaveModal={showSaveModal}
          isSuccess={isSuccess}
          handleShowSuccessModal={handleShowSuccessModal}
          handleShowSaveModal={handleShowSaveModal}
          handleShowScheduleMessageModal={handleShowScheduleMessageModal}
          error={error}
        />
      }
      {(showScheduleMessageModal || showDlcForm) && (
        <DLCForm
          isLoading={isLoading}
          isModalOpen
          dlcFormData={{
            ...tenDlcData,
            country: tenDlcData.businessPhysicalAddress.country,
            streetAddress1: tenDlcData.businessPhysicalAddress.streetAddress1,
            streetAddress2: tenDlcData.businessPhysicalAddress.streetAddress2,
            city: tenDlcData.businessPhysicalAddress.city,
            postalCode: tenDlcData.businessPhysicalAddress.postalCode,
            state: tenDlcData.businessPhysicalAddress.state,
            campaignDetailCheckbox: false,
            requestToSubmit: false,
            stockExchange: tenDlcData.stockExchange || stockExchangesOption.NYSE,
            brandRepresentativeEmail: tenDlcData.brandRepresentativeEmail,
            businessRegistrationType: tenDlcData.businessRegistrationType || businessTypeOption.EIN,
            brandRetryCount: tenDlcData.brandRetryCount,
            regionsOfOperations: tenDlcData.regionsOfOperations.map((arg) => arg),
            ...getPOCData(tenDlcData),
          }}
          businessTypes={businessTypes}
          regionsOfOperation={regionsOfOperation}
          companyStatusTypes={companyStatusTypes}
          jobPositionTypes={jobPositionTypes}
          businessIndustrys={businessIndustries}
          businessRegistrationTypes={businessRegistrationTypes}
          countriesList={countriesList}
          stockExchanges={stockExchanges}
          stepErrorArray={stepErrorArray}
          errorContainerFields={errorContainer}
          showErrors={showErrors}
          providerStatus={tenDlcData.providerStatus}
          onDLCModalClose={() =>
            onDLCModalClose({
              handleShowScheduleMessageModal,
              dispatch,
              enableDlcForm,
            })
          }
          editFormStatus={enableEditDlcForm(tenDlcData)}
          onSave={(formData) =>
            handleSubmit({
              formData,
              requestToSubmit: false,
              customerId,
              id,
              customerIds,
              error,
              reformatDlcFormData,
              handleShowSuccessModal,
              handleShowScheduleMessageModal,
              handleShowSaveModal,
              dispatch,
              createDLCForm,
            })
          }
          onSubmit={(formData) =>
            handleSubmit({
              formData,
              requestToSubmit: true,
              customerId,
              id,
              customerIds,
              error,
              reformatDlcFormData,
              handleShowSuccessModal,
              handleShowScheduleMessageModal,
              handleShowSaveModal,
              dispatch,
              createDLCForm,
              handleResetErrorContainer,
              handleResetStepErrorArray,
            })
          }
        />
      )}
    </>
  );
};
export default Sidebar;
